import { useState, useEffect, useCallback } from 'react'
import {
  isPushSupported,
  getPushSubscriptionStatus,
  subscribeToPush,
  unsubscribeFromPush,
  type PushSubscribeResult,
} from '@/lib/pushNotifications'

interface PushNotificationState {
  supported: boolean
  permissionGranted: boolean
  subscribed: boolean
  loading: boolean
  error: string | null
}

/**
 * Hook for managing push notification subscription
 */
export function usePushNotifications() {
  const [state, setState] = useState<PushNotificationState>({
    supported: false,
    permissionGranted: false,
    subscribed: false,
    loading: true,
    error: null,
  })

  // Check initial status on mount
  useEffect(() => {
    const checkStatus = async () => {
      if (!isPushSupported()) {
        setState({
          supported: false,
          permissionGranted: false,
          subscribed: false,
          loading: false,
          error: null,
        })
        return
      }

      const status = await getPushSubscriptionStatus()
      setState({
        ...status,
        loading: false,
        error: null,
      })
    }

    checkStatus()
  }, [])

  /**
   * Subscribe to push notifications.
   * Returns the full result so callers can act on the reason.
   */
  const subscribe = useCallback(async (): Promise<PushSubscribeResult> => {
    setState((prev) => ({ ...prev, loading: true, error: null }))

    try {
      const result = await subscribeToPush()

      if (result.success) {
        setState((prev) => ({
          ...prev,
          permissionGranted: true,
          subscribed: true,
          loading: false,
          error: null,
        }))
      } else {
        // Refresh actual browser state regardless of failure reason
        const status = await getPushSubscriptionStatus()
        setState({
          ...status,
          loading: false,
          error: result.message,
        })
      }

      return result
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unknown error'
      setState((prev) => ({ ...prev, loading: false, error: message }))
      return { success: false, reason: 'subscription_failed', message }
    }
  }, [])

  /**
   * Unsubscribe from push notifications
   */
  const unsubscribe = useCallback(async (): Promise<boolean> => {
    setState((prev) => ({ ...prev, loading: true, error: null }))

    try {
      const success = await unsubscribeFromPush()

      if (success) {
        setState((prev) => ({
          ...prev,
          subscribed: false,
          loading: false,
          error: null,
        }))
      } else {
        setState((prev) => ({ ...prev, loading: false, error: 'Failed to unsubscribe' }))
      }

      return success
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unknown error'
      setState((prev) => ({ ...prev, loading: false, error: message }))
      return false
    }
  }, [])

  const clearError = useCallback(() => {
    setState((prev) => ({ ...prev, error: null }))
  }, [])

  return {
    ...state,
    subscribe,
    unsubscribe,
    clearError,
  }
}
